'use client';

import { Image } from '@repo/ui/Image';
import { clsx } from 'clsx';
import { FC, useCallback, useState } from 'react';

import { getPublicAssetUrl } from '@/general/helpers/getPublicAssetUrl';
import { isEmpty } from '@/utils/isEmpty';

import { getVideoEmbed } from './VideoEmbed.helper';

type Props = {
  width?: number;
  height?: number;
  videoUrl: string;
  thumbnailImageUrl: string;
  setButtonCentered?: boolean;
};

/**
 * Dynamically embeds a Youtube or a Vimeo video with a play button
 */
export const VideoEmbed: FC<Props> = ({ height, thumbnailImageUrl, videoUrl, width, setButtonCentered }) => {
  const [onClickPlay, setOnClickPlay] = useState<boolean>(false);

  const onImageClick = useCallback(() => {
    setOnClickPlay(true);
  }, []);

  const videoEmbed = getVideoEmbed(videoUrl, onClickPlay);

  if (!videoEmbed) {
    return null;
  }

  return (
    <div className="relative m-0 block aspect-video w-full bg-black [&_iframe]:block">
      {onClickPlay || isEmpty(thumbnailImageUrl) ? (
        <iframe
          width={width ?? 1280}
          height={height ?? 720}
          src={videoEmbed}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Embedded video"
          className="video aspect-video h-full w-full border-none object-cover"
        />
      ) : (
        <button
          className="group absolute top-0 left-0 h-full w-full cursor-pointer overflow-hidden border-none p-0"
          onClick={onImageClick}
          data-testid="VIDEO_TRIGGER"
          type="button"
        >
          <Image
            src={thumbnailImageUrl}
            width={width ?? 1280}
            height={height ?? 720}
            alt="Play video"
            className="h-full w-full object-cover transition-transform duration-200 ease-in-out group-hover:scale-110 group-focus-visible:scale-110"
          />
          <div
            className={clsx(
              'absolute inset-0 m-auto size-20 overflow-hidden rounded-full backdrop-blur-sm transition-all duration-200 ease-in-out group-hover:backdrop-blur-sm group-hover:backdrop-brightness-75 group-focus-visible:backdrop-blur-sm group-focus-visible:backdrop-brightness-75 sm:size-26 md:size-30',
              { 'mx-auto': setButtonCentered },
            )}
          >
            <Image
              src={getPublicAssetUrl('/images/play-button.svg')}
              alt="Video play button"
              width={120}
              height={120}
              className="size-full"
            />
          </div>
        </button>
      )}
    </div>
  );
};
